import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Baby, Users } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface BirthsAttendedStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

const birthRanges = [
	"1-9",
	"10-19",
	"20-49",
	"50-74",
	"75-99",
	"100-199",
	"200-499",
	"500-1000",
	"1000+",
];

export const BirthsAttendedStep = ({
	onNext,
	onBack,
	initialData,
}: BirthsAttendedStepProps) => {
	const [selectedRange, setSelectedRange] = useState(
		initialData?.birthsAttended || "",
	);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedRange) {
			alert("Please select a birth range");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "births_attended",
						data: {
							births_attended: {
								births_attended_range: selectedRange,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save birth range");
			}

			onNext({ birthsAttended: selectedRange });
		} catch (error) {
			console.error("Error saving birth range:", error);
			alert("Failed to save birth range. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 2 of 15</span>
					<span className={styles.progressLabel}>Births Attended</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "20%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Baby className={styles.cardIcon} />
						Births Attended
					</CardTitle>
					<p className={styles.cardSubtitle}>
						How many births have you attended as a doula? This helps families
						understand your experience level.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
							{birthRanges.map((range) => (
								<motion.button
									key={range}
									type="button"
									onClick={() => setSelectedRange(range)}
									className={`p-4 border-2 rounded-lg text-center transition-all duration-200 font-karla ${
										selectedRange === range
											? styles.selectionCardSelected
											: styles.selectionCardUnselected
									}`}
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
								>
									<div className="flex items-center justify-center">
										<Users className="h-4 w-4 mr-2" />
										<span className="font-semibold">{range}</span>
									</div>
									<span className="text-sm mt-1 block opacity-70">births</span>
								</motion.button>
							))}
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={!selectedRange || loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
